// Composer dictation: the recording state, states and errors (C-14).
//
// Namespace `speech`; owner WP-3 (BUILD 1). Only the owner adds keys or
// translations. Placeholders are `{name}`, filled with `formatCopy`. See ./index.ts.
// The server sends codes only; ui/speech/errors.ts maps each code to a key here.
import type { NamespaceCatalog } from '@/i18n/ns';

const en = {
  start: 'Dictate',
  insert: 'Insert',
  cancelDictation: 'Cancel dictation',
  finish: 'Finish dictation',
  retry: 'Try again',
  discard: 'Discard',
  starting: 'Starting the microphone…',
  listening: 'Listening…',
  reconnecting: 'Reconnecting… Your audio is kept on this device.',
  inserted: 'Inserted',
  keptForRetry: 'The recording is kept on this device. Try again or discard it.',
  pendingInsert: 'Your dictation is ready. Insert it or discard it.',
  idleStopping: 'No speech heard. Stopping in {seconds} s.',
  limitWarning: 'Dictation stops in {seconds} s.',
  captionLabel: 'Live dictation',
  degraded: 'Switched to a backup transcription service.',
  micHeldByMeeting: 'The microphone is recording a meeting.',
  unavailable: 'Dictation is not available right now.',
  originNotAllowed:
    'This page origin is not allowed for dictation. Open Chat from the approved address.',
  providerUnavailable: 'The speech service is temporarily unavailable. Try again shortly.',
  notConfigured: 'Dictation is not set up on this server.',
  signInAgain: 'Sign in again to dictate.',
  rateLimited: 'Too many dictations. Try again in {seconds} s.',
  budgetExhausted: 'Today’s dictation allowance is used up.',
  offline: 'You are offline. The recording is kept on this device.',
  timeout: 'Transcription took too long. The recording is kept on this device.',
  noAudio: 'No speech was heard.',
  permissionDenied: 'Allow the microphone in your browser or device settings and try again.',
  insecureContext: 'Dictation needs a secure (https) connection.',
  noMicrophone: 'No microphone was found.',
  microphoneBusy: 'The microphone is in use by another app.',
  deviceLost: 'The microphone was disconnected.',
  pausedHidden: 'Dictation stopped when the page was hidden.',
  finishing: 'Finishing the text…',
  tooLong: 'Dictation reached its time limit. The recording is kept on this device.',
  unsupported: 'This browser cannot record audio for dictation.',
  companyRequired: 'Dictation is available in a company workspace.',
  replaced: 'Dictation started somewhere else. The recording is kept on this device.',
  paymentRequired:
    "Your company's usage credit is used up. An administrator can add a card, then try again.",
};

export type SpeechCopyKey = keyof typeof en;

const speechCopy: NamespaceCatalog<SpeechCopyKey> = {
  bg: {
    start: 'Диктуване',
    insert: 'Вмъкване',
    cancelDictation: 'Отказ от диктуването',
    finish: 'Завършване на диктуването',
    retry: 'Опитай отново',
    discard: 'Отхвърляне',
    starting: 'Микрофонът се стартира…',
    listening: 'Слуша…',
    reconnecting: 'Повторно свързване… Записът остава на това устройство.',
    inserted: 'Вмъкнато',
    keptForRetry: 'Записът остава на това устройство. Опитайте отново или го отхвърлете.',
    pendingInsert: 'Диктовката е готова. Вмъкнете я или я отхвърлете.',
    idleStopping: 'Не се чува говор. Спиране след {seconds} с.',
    limitWarning: 'Диктуването спира след {seconds} с.',
    captionLabel: 'Диктуване на живо',
    degraded: 'Превключено към резервна услуга за транскрипция.',
    micHeldByMeeting: 'Микрофонът записва среща.',
    unavailable: 'Диктуването в момента не е налично.',
    originNotAllowed:
      'Произходът на тази страница не е разрешен за диктуване. Отворете Chat от одобрения адрес.',
    providerUnavailable: 'Услугата за говор временно не е налична. Опитайте отново скоро.',
    notConfigured: 'Диктуването не е настроено на този сървър.',
    signInAgain: 'Влезте отново, за да диктувате.',
    rateLimited: 'Твърде много диктовки. Опитайте отново след {seconds} с.',
    budgetExhausted: 'Дневната квота за диктуване е изчерпана.',
    offline: 'Нямате връзка. Записът остава на това устройство.',
    timeout: 'Транскрипцията отне твърде дълго. Записът остава на това устройство.',
    noAudio: 'Не беше чут говор.',
    permissionDenied:
      'Разрешете микрофона в настройките на браузъра или устройството и опитайте отново.',
    insecureContext: 'Диктуването изисква защитена връзка (https).',
    noMicrophone: 'Не е открит микрофон.',
    microphoneBusy: 'Микрофонът се използва от друго приложение.',
    deviceLost: 'Микрофонът беше изключен.',
    pausedHidden: 'Диктуването спря, когато страницата беше скрита.',
    finishing: 'Текстът се завършва…',
    tooLong: 'Диктуването достигна ограничението по време. Записът остава на това устройство.',
    unsupported: 'Този браузър не може да записва звук за диктуване.',
    companyRequired: 'Диктуването е налично във фирмено работно пространство.',
    replaced: 'Диктуване започна на друго място. Записът остава на това устройство.',
    paymentRequired:
      'Кредитът за използване на фирмата ви е изчерпан. Администратор може да добави карта, след което опитайте отново.',
  },
  hr: {
    start: 'Diktiraj',
    insert: 'Umetni',
    cancelDictation: 'Odustani od diktiranja',
    finish: 'Završi diktiranje',
    retry: 'Pokušaj ponovno',
    discard: 'Odbaci',
    starting: 'Pokretanje mikrofona…',
    listening: 'Slušam…',
    reconnecting: 'Ponovno povezivanje… Snimka ostaje na ovom uređaju.',
    inserted: 'Umetnuto',
    keptForRetry: 'Snimka ostaje na ovom uređaju. Pokušajte ponovno ili je odbacite.',
    pendingInsert: 'Diktat je spreman. Umetnite ga ili ga odbacite.',
    idleStopping: 'Govor se ne čuje. Zaustavljanje za {seconds} s.',
    limitWarning: 'Diktiranje završava za {seconds} s.',
    captionLabel: 'Diktiranje uživo',
    degraded: 'Prebačeno na pričuvnu uslugu transkripcije.',
    micHeldByMeeting: 'Mikrofon snima sastanak.',
    unavailable: 'Diktiranje trenutačno nije dostupno.',
    originNotAllowed:
      'Izvor ove stranice nije dopušten za diktiranje. Otvorite Chat s odobrene adrese.',
    providerUnavailable: 'Govorna usluga privremeno nije dostupna. Pokušajte ponovno uskoro.',
    notConfigured: 'Diktiranje nije postavljeno na ovom poslužitelju.',
    signInAgain: 'Ponovno se prijavite za diktiranje.',
    rateLimited: 'Previše diktiranja. Pokušajte ponovno za {seconds} s.',
    budgetExhausted: 'Današnja kvota diktiranja je potrošena.',
    offline: 'Niste povezani. Snimka ostaje na ovom uređaju.',
    timeout: 'Transkripcija je trajala predugo. Snimka ostaje na ovom uređaju.',
    noAudio: 'Nije se čuo govor.',
    permissionDenied:
      'Dopustite mikrofon u postavkama preglednika ili uređaja i pokušajte ponovno.',
    insecureContext: 'Diktiranje zahtijeva sigurnu vezu (https).',
    noMicrophone: 'Mikrofon nije pronađen.',
    microphoneBusy: 'Mikrofon koristi druga aplikacija.',
    deviceLost: 'Mikrofon je isključen.',
    pausedHidden: 'Diktiranje je zaustavljeno kad je stranica skrivena.',
    finishing: 'Dovršavanje teksta…',
    tooLong: 'Diktiranje je doseglo vremensko ograničenje. Snimka ostaje na ovom uređaju.',
    unsupported: 'Ovaj preglednik ne može snimati zvuk za diktiranje.',
    companyRequired: 'Diktiranje je dostupno u radnom prostoru tvrtke.',
    replaced: 'Diktiranje je pokrenuto negdje drugdje. Snimka ostaje na ovom uređaju.',
    paymentRequired:
      'Kredit za korištenje vaše tvrtke je potrošen. Administrator može dodati karticu, a zatim pokušajte ponovno.',
  },
  cs: {
    start: 'Diktovat',
    insert: 'Vložit',
    cancelDictation: 'Zrušit diktování',
    finish: 'Dokončit diktování',
    retry: 'Zkusit znovu',
    discard: 'Zahodit',
    starting: 'Spouštění mikrofonu…',
    listening: 'Poslouchám…',
    reconnecting: 'Obnovování připojení… Nahrávka zůstává v tomto zařízení.',
    inserted: 'Vloženo',
    keptForRetry: 'Nahrávka zůstává v tomto zařízení. Zkuste to znovu, nebo ji zahoďte.',
    pendingInsert: 'Diktát je připravený. Vložte ho, nebo ho zahoďte.',
    idleStopping: 'Není slyšet řeč. Zastavení za {seconds} s.',
    limitWarning: 'Diktování skončí za {seconds} s.',
    captionLabel: 'Živé diktování',
    degraded: 'Přepnuto na záložní službu přepisu.',
    micHeldByMeeting: 'Mikrofon nahrává schůzku.',
    unavailable: 'Diktování teď není k dispozici.',
    originNotAllowed:
      'Původ této stránky není pro diktování povolen. Otevřete Chat ze schválené adresy.',
    providerUnavailable: 'Řečová služba je dočasně nedostupná. Zkuste to brzy znovu.',
    notConfigured: 'Diktování není na tomto serveru nastavené.',
    signInAgain: 'Pro diktování se znovu přihlaste.',
    rateLimited: 'Příliš mnoho diktování. Zkuste to znovu za {seconds} s.',
    budgetExhausted: 'Dnešní limit diktování je vyčerpaný.',
    offline: 'Jste offline. Nahrávka zůstává v tomto zařízení.',
    timeout: 'Přepis trval příliš dlouho. Nahrávka zůstává v tomto zařízení.',
    noAudio: 'Nebyla slyšet žádná řeč.',
    permissionDenied: 'Povolte mikrofon v nastavení prohlížeče nebo zařízení a zkuste to znovu.',
    insecureContext: 'Diktování vyžaduje zabezpečené připojení (https).',
    noMicrophone: 'Nebyl nalezen žádný mikrofon.',
    microphoneBusy: 'Mikrofon používá jiná aplikace.',
    deviceLost: 'Mikrofon byl odpojen.',
    pausedHidden: 'Diktování se zastavilo, když byla stránka skryta.',
    finishing: 'Dokončování textu…',
    tooLong: 'Diktování dosáhlo časového limitu. Nahrávka zůstává v tomto zařízení.',
    unsupported: 'Tento prohlížeč nemůže nahrávat zvuk pro diktování.',
    companyRequired: 'Diktování je k dispozici ve firemním pracovním prostoru.',
    replaced: 'Diktování bylo zahájeno jinde. Nahrávka zůstává v tomto zařízení.',
    paymentRequired:
      'Kredit na využití vaší firmy je vyčerpaný. Správce může přidat kartu, pak to zkuste znovu.',
  },
  da: {
    start: 'Diktér',
    insert: 'Indsæt',
    cancelDictation: 'Annuller diktering',
    finish: 'Afslut diktering',
    retry: 'Prøv igen',
    discard: 'Kassér',
    starting: 'Starter mikrofonen…',
    listening: 'Lytter…',
    reconnecting: 'Genopretter forbindelsen… Optagelsen er gemt på denne enhed.',
    inserted: 'Indsat',
    keptForRetry: 'Optagelsen er gemt på denne enhed. Prøv igen, eller kassér den.',
    pendingInsert: 'Din diktering er klar. Indsæt den, eller kassér den.',
    idleStopping: 'Der høres ingen tale. Stopper om {seconds} s.',
    limitWarning: 'Dikteringen stopper om {seconds} s.',
    captionLabel: 'Diktering i realtid',
    degraded: 'Skiftede til en reservetjeneste til transskription.',
    micHeldByMeeting: 'Mikrofonen optager et møde.',
    unavailable: 'Diktering er ikke tilgængelig lige nu.',
    originNotAllowed:
      'Denne sideoprindelse er ikke tilladt til diktering. Åbn Chat fra den godkendte adresse.',
    providerUnavailable: 'Talefunktion er midlertidigt utilgængelig. Prøv igen om lidt.',
    notConfigured: 'Diktering er ikke sat op på denne server.',
    signInAgain: 'Log ind igen for at diktere.',
    rateLimited: 'For mange dikteringer. Prøv igen om {seconds} s.',
    budgetExhausted: 'Dagens dikteringskvote er brugt op.',
    offline: 'Du er offline. Optagelsen er gemt på denne enhed.',
    timeout: 'Transskriptionen tog for lang tid. Optagelsen er gemt på denne enhed.',
    noAudio: 'Der blev ikke hørt nogen tale.',
    permissionDenied: 'Tillad mikrofonen i browserens eller enhedens indstillinger, og prøv igen.',
    insecureContext: 'Diktering kræver en sikker forbindelse (https).',
    noMicrophone: 'Der blev ikke fundet nogen mikrofon.',
    microphoneBusy: 'Mikrofonen bruges af en anden app.',
    deviceLost: 'Forbindelsen til mikrofonen blev afbrudt.',
    pausedHidden: 'Dikteringen stoppede, da siden blev skjult.',
    finishing: 'Gør teksten færdig…',
    tooLong: 'Dikteringen nåede tidsgrænsen. Optagelsen er gemt på denne enhed.',
    unsupported: 'Denne browser kan ikke optage lyd til diktering.',
    companyRequired: 'Diktering er tilgængelig i et virksomhedsarbejdsområde.',
    replaced: 'En diktering blev startet et andet sted. Optagelsen er gemt på denne enhed.',
    paymentRequired:
      'Jeres virksomheds forbrugskredit er brugt op. En administrator kan tilføje et kort; prøv derefter igen.',
  },
  nl: {
    start: 'Dicteren',
    insert: 'Invoegen',
    cancelDictation: 'Dicteren annuleren',
    finish: 'Dicteren afronden',
    retry: 'Opnieuw proberen',
    discard: 'Weggooien',
    starting: 'Microfoon wordt gestart…',
    listening: 'Luistert…',
    reconnecting: 'Opnieuw verbinden… De opname blijft op dit apparaat.',
    inserted: 'Ingevoegd',
    keptForRetry: 'De opname blijft op dit apparaat. Probeer het opnieuw of gooi haar weg.',
    pendingInsert: 'Je dictaat is klaar. Voeg het in of gooi het weg.',
    idleStopping: 'Geen spraak gehoord. Stopt over {seconds} s.',
    limitWarning: 'Het dicteren stopt over {seconds} s.',
    captionLabel: 'Live dictaat',
    degraded: 'Overgeschakeld naar een reservedienst voor transcriptie.',
    micHeldByMeeting: 'De microfoon neemt een vergadering op.',
    unavailable: 'Dicteren is nu niet beschikbaar.',
    originNotAllowed:
      'Deze pagina-oorsprong is niet toegestaan voor dicteren. Open Chat vanaf het goedgekeurde adres.',
    providerUnavailable: 'De spraakdienst is tijdelijk niet beschikbaar. Probeer het zo opnieuw.',
    notConfigured: 'Dicteren is niet ingesteld op deze server.',
    signInAgain: 'Log opnieuw in om te dicteren.',
    rateLimited: 'Te veel dictaten. Probeer het over {seconds} s opnieuw.',
    budgetExhausted: 'Het dicteertegoed van vandaag is op.',
    offline: 'Je bent offline. De opname blijft op dit apparaat.',
    timeout: 'De transcriptie duurde te lang. De opname blijft op dit apparaat.',
    noAudio: 'Er werd geen spraak gehoord.',
    permissionDenied:
      'Sta de microfoon toe in de instellingen van je browser of apparaat en probeer het opnieuw.',
    insecureContext: 'Dicteren vereist een beveiligde verbinding (https).',
    noMicrophone: 'Er is geen microfoon gevonden.',
    microphoneBusy: 'De microfoon wordt door een andere app gebruikt.',
    deviceLost: 'De microfoon is losgekoppeld.',
    pausedHidden: 'Het dicteren is gestopt toen de pagina werd verborgen.',
    finishing: 'Tekst wordt afgerond…',
    tooLong: 'Het dicteren heeft de tijdslimiet bereikt. De opname blijft op dit apparaat.',
    unsupported: 'Deze browser kan geen geluid opnemen om te dicteren.',
    companyRequired: 'Dicteren is beschikbaar in een bedrijfswerkruimte.',
    replaced: 'Er is ergens anders een dictaat gestart. De opname blijft op dit apparaat.',
    paymentRequired:
      'Het gebruikstegoed van je bedrijf is op. Een beheerder kan een kaart toevoegen; probeer het daarna opnieuw.',
  },
  en,
  et: {
    start: 'Dikteeri',
    insert: 'Lisa',
    cancelDictation: 'Tühista dikteerimine',
    finish: 'Lõpeta dikteerimine',
    retry: 'Proovi uuesti',
    discard: 'Loobu',
    starting: 'Mikrofoni käivitamine…',
    listening: 'Kuulan…',
    reconnecting: 'Ühendan uuesti… Salvestis jääb sellesse seadmesse.',
    inserted: 'Lisatud',
    keptForRetry: 'Salvestis jääb sellesse seadmesse. Proovi uuesti või loobu sellest.',
    pendingInsert: 'Dikteering on valmis. Lisa see või loobu sellest.',
    idleStopping: 'Kõnet ei kuule. Peatub {seconds} s pärast.',
    limitWarning: 'Dikteerimine lõpeb {seconds} s pärast.',
    captionLabel: 'Reaalajas dikteerimine',
    degraded: 'Kasutusel on varu-transkriptsiooniteenus.',
    micHeldByMeeting: 'Mikrofon salvestab koosolekut.',
    unavailable: 'Dikteerimine pole praegu saadaval.',
    originNotAllowed:
      'Selle lehe päritolu pole dikteerimiseks lubatud. Ava Chat heakskiidetud aadressilt.',
    providerUnavailable: 'Kõneteenus pole ajutiselt saadaval. Proovi varsti uuesti.',
    notConfigured: 'Dikteerimine pole selles serveris seadistatud.',
    signInAgain: 'Dikteerimiseks logi uuesti sisse.',
    rateLimited: 'Liiga palju dikteerimisi. Proovi uuesti {seconds} s pärast.',
    budgetExhausted: 'Tänane dikteerimiskvoot on täis.',
    offline: 'Oled võrguühenduseta. Salvestis jääb sellesse seadmesse.',
    timeout: 'Transkribeerimine võttis liiga kaua aega. Salvestis jääb sellesse seadmesse.',
    noAudio: 'Kõnet ei kuuldud.',
    permissionDenied: 'Luba mikrofon brauseri või seadme seadetes ja proovi uuesti.',
    insecureContext: 'Dikteerimine vajab turvalist ühendust (https).',
    noMicrophone: 'Mikrofoni ei leitud.',
    microphoneBusy: 'Mikrofon on teise rakenduse kasutuses.',
    deviceLost: 'Mikrofoni ühendus katkes.',
    pausedHidden: 'Dikteerimine peatus, kui leht peideti.',
    finishing: 'Viimistlen teksti…',
    tooLong: 'Dikteerimine jõudis ajapiiranguni. Salvestis jääb sellesse seadmesse.',
    unsupported: 'See brauser ei saa dikteerimiseks heli salvestada.',
    companyRequired: 'Dikteerimine on saadaval ettevõtte tööruumis.',
    replaced: 'Dikteerimist alustati mujal. Salvestis jääb sellesse seadmesse.',
    paymentRequired:
      'Ettevõtte kasutuskrediit on otsas. Administraator saab lisada kaardi, seejärel proovi uuesti.',
  },
  fi: {
    start: 'Sanele',
    insert: 'Lisää',
    cancelDictation: 'Peruuta sanelu',
    finish: 'Lopeta sanelu',
    retry: 'Yritä uudelleen',
    discard: 'Hylkää',
    starting: 'Käynnistetään mikrofonia…',
    listening: 'Kuunnellaan…',
    reconnecting: 'Yhdistetään uudelleen… Tallenne säilyy tällä laitteella.',
    inserted: 'Lisätty',
    keptForRetry: 'Tallenne säilyy tällä laitteella. Yritä uudelleen tai hylkää se.',
    pendingInsert: 'Sanelusi on valmis. Lisää tai hylkää se.',
    idleStopping: 'Puhetta ei kuulu. Pysähtyy {seconds} s kuluttua.',
    limitWarning: 'Sanelu päättyy {seconds} s kuluttua.',
    captionLabel: 'Reaaliaikainen sanelu',
    degraded: 'Vaihdettiin varalla olevaan litterointipalveluun.',
    micHeldByMeeting: 'Mikrofoni tallentaa kokousta.',
    unavailable: 'Sanelu ei ole juuri nyt käytettävissä.',
    originNotAllowed:
      'Tämän sivun alkuperää ei sallita saneluun. Avaa Chat hyväksytystä osoitteesta.',
    providerUnavailable: 'Puhepalvelu ei ole tilapäisesti käytettävissä. Yritä pian uudelleen.',
    notConfigured: 'Sanelua ei ole määritetty tälle palvelimelle.',
    signInAgain: 'Kirjaudu uudelleen sisään sanellaksesi.',
    rateLimited: 'Liian monta sanelua. Yritä uudelleen {seconds} s kuluttua.',
    budgetExhausted: 'Tämän päivän sanelukiintiö on käytetty.',
    offline: 'Olet offline-tilassa. Tallenne säilyy tällä laitteella.',
    timeout: 'Litterointi kesti liian kauan. Tallenne säilyy tällä laitteella.',
    noAudio: 'Puhetta ei kuulunut.',
    permissionDenied: 'Salli mikrofoni selaimen tai laitteen asetuksissa ja yritä uudelleen.',
    insecureContext: 'Sanelu vaatii suojatun yhteyden (https).',
    noMicrophone: 'Mikrofonia ei löytynyt.',
    microphoneBusy: 'Mikrofoni on toisen sovelluksen käytössä.',
    deviceLost: 'Mikrofonin yhteys katkesi.',
    pausedHidden: 'Sanelu pysähtyi, kun sivu piilotettiin.',
    finishing: 'Viimeistellään tekstiä…',
    tooLong: 'Sanelu saavutti aikarajansa. Tallenne säilyy tällä laitteella.',
    unsupported: 'Tämä selain ei voi tallentaa ääntä sanelua varten.',
    companyRequired: 'Sanelu on käytettävissä yrityksen työtilassa.',
    replaced: 'Sanelu aloitettiin muualla. Tallenne säilyy tällä laitteella.',
    paymentRequired:
      'Yrityksesi käyttösaldo on käytetty. Ylläpitäjä voi lisätä kortin, yritä sitten uudelleen.',
  },
  fr: {
    start: 'Dicter',
    insert: 'Insérer',
    cancelDictation: 'Annuler la dictée',
    finish: 'Terminer la dictée',
    retry: 'Réessayer',
    discard: 'Supprimer',
    starting: 'Démarrage du micro…',
    listening: 'Écoute…',
    reconnecting: 'Reconnexion… L’enregistrement est conservé sur cet appareil.',
    inserted: 'Inséré',
    keptForRetry: 'L’enregistrement est conservé sur cet appareil. Réessayez ou supprimez-le.',
    pendingInsert: 'Votre dictée est prête. Insérez-la ou supprimez-la.',
    idleStopping: 'Aucune parole détectée. Arrêt dans {seconds} s.',
    limitWarning: 'La dictée s’arrête dans {seconds} s.',
    captionLabel: 'Dictée en direct',
    degraded: 'Passage à un service de transcription de secours.',
    micHeldByMeeting: 'Le micro enregistre une réunion.',
    unavailable: 'La dictée n’est pas disponible pour le moment.',
    originNotAllowed:
      'L’origine de cette page n’est pas autorisée pour la dictée. Ouvrez Chat depuis l’adresse approuvée.',
    providerUnavailable: 'Le service vocal est temporairement indisponible. Réessayez bientôt.',
    notConfigured: 'La dictée n’est pas configurée sur ce serveur.',
    signInAgain: 'Reconnectez-vous pour dicter.',
    rateLimited: 'Trop de dictées. Réessayez dans {seconds} s.',
    budgetExhausted: 'Le quota de dictée du jour est épuisé.',
    offline: 'Vous êtes hors ligne. L’enregistrement est conservé sur cet appareil.',
    timeout:
      'La transcription a pris trop de temps. L’enregistrement est conservé sur cet appareil.',
    noAudio: 'Aucune parole n’a été détectée.',
    permissionDenied:
      'Autorisez le micro dans les réglages du navigateur ou de l’appareil, puis réessayez.',
    insecureContext: 'La dictée nécessite une connexion sécurisée (https).',
    noMicrophone: 'Aucun micro n’a été trouvé.',
    microphoneBusy: 'Le micro est utilisé par une autre application.',
    deviceLost: 'Le micro a été déconnecté.',
    pausedHidden: 'La dictée s’est arrêtée quand la page a été masquée.',
    finishing: 'Finalisation du texte…',
    tooLong:
      'La dictée a atteint sa durée maximale. L’enregistrement est conservé sur cet appareil.',
    unsupported: 'Ce navigateur ne peut pas enregistrer l’audio pour la dictée.',
    companyRequired: 'La dictée est disponible dans un espace de travail d’entreprise.',
    replaced: 'Une dictée a démarré ailleurs. L’enregistrement est conservé sur cet appareil.',
    paymentRequired:
      'Le crédit d’utilisation de votre entreprise est épuisé. Un administrateur peut ajouter une carte ; réessayez ensuite.',
  },
  de: {
    start: 'Diktieren',
    insert: 'Einfügen',
    cancelDictation: 'Diktat abbrechen',
    finish: 'Diktat beenden',
    retry: 'Erneut versuchen',
    discard: 'Verwerfen',
    starting: 'Mikrofon wird gestartet…',
    listening: 'Hört zu…',
    reconnecting: 'Verbindung wird wiederhergestellt… Die Aufnahme bleibt auf diesem Gerät.',
    inserted: 'Eingefügt',
    keptForRetry:
      'Die Aufnahme bleibt auf diesem Gerät. Versuchen Sie es erneut oder verwerfen Sie sie.',
    pendingInsert: 'Ihr Diktat ist fertig. Fügen Sie es ein oder verwerfen Sie es.',
    idleStopping: 'Keine Sprache erkannt. Stopp in {seconds} s.',
    limitWarning: 'Das Diktat endet in {seconds} s.',
    captionLabel: 'Live-Diktat',
    degraded: 'Zu einem Ersatzdienst für die Transkription gewechselt.',
    micHeldByMeeting: 'Das Mikrofon nimmt gerade ein Meeting auf.',
    unavailable: 'Diktieren ist gerade nicht verfügbar.',
    originNotAllowed:
      'Diese Seitenherkunft ist für das Diktieren nicht zugelassen. Öffnen Sie Chat über die genehmigte Adresse.',
    providerUnavailable:
      'Der Sprachdienst ist vorübergehend nicht verfügbar. Versuchen Sie es bald erneut.',
    notConfigured: 'Diktieren ist auf diesem Server nicht eingerichtet.',
    signInAgain: 'Melden Sie sich erneut an, um zu diktieren.',
    rateLimited: 'Zu viele Diktate. Versuchen Sie es in {seconds} s erneut.',
    budgetExhausted: 'Das heutige Diktierkontingent ist aufgebraucht.',
    offline: 'Sie sind offline. Die Aufnahme bleibt auf diesem Gerät.',
    timeout: 'Die Transkription hat zu lange gedauert. Die Aufnahme bleibt auf diesem Gerät.',
    noAudio: 'Es wurde keine Sprache erkannt.',
    permissionDenied:
      'Erlauben Sie das Mikrofon in den Browser- oder Geräteeinstellungen und versuchen Sie es erneut.',
    insecureContext: 'Diktieren erfordert eine sichere Verbindung (https).',
    noMicrophone: 'Es wurde kein Mikrofon gefunden.',
    microphoneBusy: 'Das Mikrofon wird von einer anderen App verwendet.',
    deviceLost: 'Die Verbindung zum Mikrofon wurde getrennt.',
    pausedHidden: 'Das Diktat wurde beendet, als die Seite ausgeblendet wurde.',
    finishing: 'Text wird fertiggestellt…',
    tooLong: 'Das Diktat hat sein Zeitlimit erreicht. Die Aufnahme bleibt auf diesem Gerät.',
    unsupported: 'Dieser Browser kann keinen Ton zum Diktieren aufnehmen.',
    companyRequired: 'Diktieren ist in einem Firmen-Arbeitsbereich verfügbar.',
    replaced: 'An anderer Stelle wurde ein Diktat gestartet. Die Aufnahme bleibt auf diesem Gerät.',
    paymentRequired:
      'Das Nutzungsguthaben Ihres Unternehmens ist aufgebraucht. Ein Administrator kann eine Karte hinzufügen; versuchen Sie es dann erneut.',
  },
  el: {
    start: 'Υπαγόρευση',
    insert: 'Εισαγωγή',
    cancelDictation: 'Ακύρωση υπαγόρευσης',
    finish: 'Ολοκλήρωση υπαγόρευσης',
    retry: 'Δοκιμάστε ξανά',
    discard: 'Απόρριψη',
    starting: 'Εκκίνηση μικροφώνου…',
    listening: 'Ακρόαση…',
    reconnecting: 'Επανασύνδεση… Η ηχογράφηση παραμένει σε αυτή τη συσκευή.',
    inserted: 'Εισήχθη',
    keptForRetry: 'Η ηχογράφηση παραμένει σε αυτή τη συσκευή. Δοκιμάστε ξανά ή απορρίψτε την.',
    pendingInsert: 'Η υπαγόρευση είναι έτοιμη. Εισαγάγετέ την ή απορρίψτε την.',
    idleStopping: 'Δεν ακούγεται ομιλία. Διακοπή σε {seconds} δ.',
    limitWarning: 'Η υπαγόρευση σταματά σε {seconds} δ.',
    captionLabel: 'Ζωντανή υπαγόρευση',
    degraded: 'Έγινε μετάβαση σε εφεδρική υπηρεσία μεταγραφής.',
    micHeldByMeeting: 'Το μικρόφωνο ηχογραφεί μια σύσκεψη.',
    unavailable: 'Η υπαγόρευση δεν είναι διαθέσιμη αυτή τη στιγμή.',
    originNotAllowed:
      'Η προέλευση αυτής της σελίδας δεν επιτρέπεται για υπαγόρευση. Ανοίξτε το Chat από την εγκεκριμένη διεύθυνση.',
    providerUnavailable:
      'Η υπηρεσία ομιλίας δεν είναι προσωρινά διαθέσιμη. Δοκιμάστε ξανά σύντομα.',
    notConfigured: 'Η υπαγόρευση δεν έχει ρυθμιστεί σε αυτόν τον διακομιστή.',
    signInAgain: 'Συνδεθείτε ξανά για να υπαγορεύσετε.',
    rateLimited: 'Πάρα πολλές υπαγορεύσεις. Δοκιμάστε ξανά σε {seconds} δ.',
    budgetExhausted: 'Το σημερινό όριο υπαγόρευσης εξαντλήθηκε.',
    offline: 'Είστε εκτός σύνδεσης. Η ηχογράφηση παραμένει σε αυτή τη συσκευή.',
    timeout: 'Η μεταγραφή διήρκεσε πολύ. Η ηχογράφηση παραμένει σε αυτή τη συσκευή.',
    noAudio: 'Δεν ακούστηκε ομιλία.',
    permissionDenied:
      'Επιτρέψτε το μικρόφωνο στις ρυθμίσεις του προγράμματος περιήγησης ή της συσκευής και δοκιμάστε ξανά.',
    insecureContext: 'Η υπαγόρευση απαιτεί ασφαλή σύνδεση (https).',
    noMicrophone: 'Δεν βρέθηκε μικρόφωνο.',
    microphoneBusy: 'Το μικρόφωνο χρησιμοποιείται από άλλη εφαρμογή.',
    deviceLost: 'Το μικρόφωνο αποσυνδέθηκε.',
    pausedHidden: 'Η υπαγόρευση σταμάτησε όταν η σελίδα αποκρύφτηκε.',
    finishing: 'Ολοκλήρωση κειμένου…',
    tooLong: 'Η υπαγόρευση έφτασε το χρονικό όριο. Η ηχογράφηση παραμένει σε αυτή τη συσκευή.',
    unsupported: 'Αυτό το πρόγραμμα περιήγησης δεν μπορεί να ηχογραφήσει για υπαγόρευση.',
    companyRequired: 'Η υπαγόρευση είναι διαθέσιμη σε χώρο εργασίας εταιρείας.',
    replaced: 'Ξεκίνησε υπαγόρευση κάπου αλλού. Η ηχογράφηση παραμένει σε αυτή τη συσκευή.',
    paymentRequired:
      'Η πίστωση χρήσης της εταιρείας σας εξαντλήθηκε. Ένας διαχειριστής μπορεί να προσθέσει κάρτα· στη συνέχεια δοκιμάστε ξανά.',
  },
  hu: {
    start: 'Diktálás',
    insert: 'Beszúrás',
    cancelDictation: 'Diktálás megszakítása',
    finish: 'Diktálás befejezése',
    retry: 'Újra',
    discard: 'Elvetés',
    starting: 'Mikrofon indítása…',
    listening: 'Figyelek…',
    reconnecting: 'Újracsatlakozás… A felvétel ezen az eszközön marad.',
    inserted: 'Beszúrva',
    keptForRetry: 'A felvétel ezen az eszközön marad. Próbálja újra, vagy vesse el.',
    pendingInsert: 'A diktálás elkészült. Szúrja be vagy vesse el.',
    idleStopping: 'Nem hallható beszéd. Leállás {seconds} mp múlva.',
    limitWarning: 'A diktálás {seconds} mp múlva leáll.',
    captionLabel: 'Élő diktálás',
    degraded: 'Átváltva egy tartalék átírási szolgáltatásra.',
    micHeldByMeeting: 'A mikrofon egy megbeszélést rögzít.',
    unavailable: 'A diktálás jelenleg nem érhető el.',
    originNotAllowed:
      'Ez az oldalforrás nem engedélyezett diktáláshoz. Nyissa meg a Chatet a jóváhagyott címről.',
    providerUnavailable: 'A beszédszolgáltatás átmenetileg nem érhető el. Próbálja újra hamarosan.',
    notConfigured: 'A diktálás nincs beállítva ezen a szerveren.',
    signInAgain: 'A diktáláshoz jelentkezzen be újra.',
    rateLimited: 'Túl sok diktálás. Próbálja újra {seconds} mp múlva.',
    budgetExhausted: 'A mai diktálási keret elfogyott.',
    offline: 'Nincs internetkapcsolat. A felvétel ezen az eszközön marad.',
    timeout: 'Az átírás túl sokáig tartott. A felvétel ezen az eszközön marad.',
    noAudio: 'Nem hallatszott beszéd.',
    permissionDenied:
      'Engedélyezze a mikrofont a böngésző vagy az eszköz beállításaiban, majd próbálja újra.',
    insecureContext: 'A diktáláshoz biztonságos kapcsolat (https) szükséges.',
    noMicrophone: 'Nem található mikrofon.',
    microphoneBusy: 'A mikrofont egy másik alkalmazás használja.',
    deviceLost: 'A mikrofon kapcsolata megszakadt.',
    pausedHidden: 'A diktálás leállt, amikor az oldal rejtve lett.',
    finishing: 'A szöveg véglegesítése…',
    tooLong: 'A diktálás elérte az időkorlátot. A felvétel ezen az eszközön marad.',
    unsupported: 'Ez a böngésző nem tud hangot rögzíteni diktáláshoz.',
    companyRequired: 'A diktálás céges munkaterületen érhető el.',
    replaced: 'Máshol diktálás indult. A felvétel ezen az eszközön marad.',
    paymentRequired:
      'A cég felhasználási kerete elfogyott. Egy adminisztrátor hozzáadhat egy kártyát, majd próbálja újra.',
  },
  ga: {
    start: 'Deachtaigh',
    insert: 'Ionsáigh',
    cancelDictation: 'Cealaigh an deachtú',
    finish: 'Críochnaigh an deachtú',
    retry: 'Bain triail eile as',
    discard: 'Caith uait',
    starting: 'An micreafón á thosú…',
    listening: 'Ag éisteacht…',
    reconnecting: 'Ag athcheangal… Fanann an taifeadadh ar an ngléas seo.',
    inserted: 'Ionsáite',
    keptForRetry: 'Fanann an taifeadadh ar an ngléas seo. Bain triail eile as nó caith uait é.',
    pendingInsert: 'Tá do dheachtú réidh. Ionsáigh é nó caith uait é.',
    idleStopping: 'Níl caint le cloisteáil. Stopfar i gceann {seconds} s.',
    limitWarning: 'Stopfaidh an deachtú i gceann {seconds} s.',
    captionLabel: 'Deachtú beo',
    degraded: 'Aistríodh chuig seirbhís tras-scríofa chúltaca.',
    micHeldByMeeting: 'Tá an micreafón ag taifeadadh cruinnithe.',
    unavailable: 'Níl an deachtú ar fáil faoi láthair.',
    originNotAllowed:
      'Ní cheadaítear bunús an leathanaigh seo le haghaidh deachtaithe. Oscail Chat ón seoladh ceadaithe.',
    providerUnavailable:
      'Níl an tseirbhís urlabhra ar fáil go sealadach. Bain triail eile as gan mhoill.',
    notConfigured: 'Níl an deachtú socraithe ar an bhfreastalaí seo.',
    signInAgain: 'Sínigh isteach arís chun deachtú.',
    rateLimited: 'An iomarca deachtuithe. Bain triail eile as i gceann {seconds} s.',
    budgetExhausted: 'Tá cuóta deachtaithe an lae inniu ídithe.',
    offline: 'Tá tú as líne. Fanann an taifeadadh ar an ngléas seo.',
    timeout: 'Thóg an tras-scríobh rófhada. Fanann an taifeadadh ar an ngléas seo.',
    noAudio: 'Níor cloiseadh aon chaint.',
    permissionDenied:
      'Ceadaigh an micreafón i socruithe an bhrabhsálaí nó an ghléis agus bain triail eile as.',
    insecureContext: 'Teastaíonn nasc slán (https) le haghaidh deachtaithe.',
    noMicrophone: 'Níor aimsíodh aon mhicreafón.',
    microphoneBusy: 'Tá aip eile ag úsáid an mhicreafóin.',
    deviceLost: 'Dícheanglaíodh an micreafón.',
    pausedHidden: 'Stopadh an deachtú nuair a folaíodh an leathanach.',
    finishing: 'An téacs á chríochnú…',
    tooLong: 'Shroich an deachtú a theorainn ama. Fanann an taifeadadh ar an ngléas seo.',
    unsupported: 'Ní féidir leis an mbrabhsálaí seo fuaim a thaifeadadh le haghaidh deachtaithe.',
    companyRequired: 'Tá an deachtú ar fáil i spás oibre cuideachta.',
    replaced: 'Cuireadh tús le deachtú in áit eile. Fanann an taifeadadh ar an ngléas seo.',
    paymentRequired:
      'Tá creidmheas úsáide do chuideachta ídithe. Is féidir le riarthóir cárta a chur leis, agus ansin bain triail eile as.',
  },
  it: {
    start: 'Detta',
    insert: 'Inserisci',
    cancelDictation: 'Annulla la dettatura',
    finish: 'Termina la dettatura',
    retry: 'Riprova',
    discard: 'Elimina',
    starting: 'Avvio del microfono…',
    listening: 'In ascolto…',
    reconnecting: 'Riconnessione… La registrazione resta su questo dispositivo.',
    inserted: 'Inserito',
    keptForRetry: 'La registrazione resta su questo dispositivo. Riprova o eliminala.',
    pendingInsert: 'La dettatura è pronta. Inseriscila o eliminala.',
    idleStopping: 'Nessuna voce rilevata. Interruzione tra {seconds} s.',
    limitWarning: 'La dettatura si interrompe tra {seconds} s.',
    captionLabel: 'Dettatura in diretta',
    degraded: 'Passato a un servizio di trascrizione di riserva.',
    micHeldByMeeting: 'Il microfono sta registrando una riunione.',
    unavailable: 'La dettatura non è disponibile al momento.',
    originNotAllowed:
      'L’origine di questa pagina non è autorizzata per la dettatura. Apri Chat dall’indirizzo approvato.',
    providerUnavailable: 'Il servizio vocale non è temporaneamente disponibile. Riprova tra poco.',
    notConfigured: 'La dettatura non è configurata su questo server.',
    signInAgain: 'Accedi di nuovo per dettare.',
    rateLimited: 'Troppe dettature. Riprova tra {seconds} s.',
    budgetExhausted: 'La quota di dettatura di oggi è esaurita.',
    offline: 'Sei offline. La registrazione resta su questo dispositivo.',
    timeout:
      'La trascrizione ha richiesto troppo tempo. La registrazione resta su questo dispositivo.',
    noAudio: 'Non è stata rilevata alcuna voce.',
    permissionDenied:
      'Consenti il microfono nelle impostazioni del browser o del dispositivo e riprova.',
    insecureContext: 'La dettatura richiede una connessione sicura (https).',
    noMicrophone: 'Nessun microfono trovato.',
    microphoneBusy: 'Il microfono è in uso da un’altra app.',
    deviceLost: 'Il microfono è stato scollegato.',
    pausedHidden: 'La dettatura si è interrotta quando la pagina è stata nascosta.',
    finishing: 'Completamento del testo…',
    tooLong:
      'La dettatura ha raggiunto il limite di tempo. La registrazione resta su questo dispositivo.',
    unsupported: 'Questo browser non può registrare audio per la dettatura.',
    companyRequired: 'La dettatura è disponibile in uno spazio di lavoro aziendale.',
    replaced:
      'È stata avviata una dettatura altrove. La registrazione resta su questo dispositivo.',
    paymentRequired:
      'Il credito di utilizzo della tua azienda è esaurito. Un amministratore può aggiungere una carta, poi riprova.',
  },
  lv: {
    start: 'Diktēt',
    insert: 'Ievietot',
    cancelDictation: 'Atcelt diktēšanu',
    finish: 'Pabeigt diktēšanu',
    retry: 'Mēģināt vēlreiz',
    discard: 'Atmest',
    starting: 'Startē mikrofonu…',
    listening: 'Klausās…',
    reconnecting: 'Atkārtoti savienojas… Ieraksts paliek šajā ierīcē.',
    inserted: 'Ievietots',
    keptForRetry: 'Ieraksts paliek šajā ierīcē. Mēģiniet vēlreiz vai atmetiet to.',
    pendingInsert: 'Diktāts ir gatavs. Ievietojiet vai atmetiet to.',
    idleStopping: 'Runa nav dzirdama. Apturēs pēc {seconds} s.',
    limitWarning: 'Diktēšana beigsies pēc {seconds} s.',
    captionLabel: 'Tiešraides diktēšana',
    degraded: 'Pārslēgts uz rezerves transkripcijas pakalpojumu.',
    micHeldByMeeting: 'Mikrofons ieraksta sapulci.',
    unavailable: 'Diktēšana pašlaik nav pieejama.',
    originNotAllowed:
      'Šīs lapas izcelsme nav atļauta diktēšanai. Atveriet Chat no apstiprinātās adreses.',
    providerUnavailable: 'Runas pakalpojums īslaicīgi nav pieejams. Drīzumā mēģiniet vēlreiz.',
    notConfigured: 'Diktēšana šajā serverī nav iestatīta.',
    signInAgain: 'Piesakieties vēlreiz, lai diktētu.',
    rateLimited: 'Pārāk daudz diktātu. Mēģiniet vēlreiz pēc {seconds} s.',
    budgetExhausted: 'Šodienas diktēšanas kvota ir izlietota.',
    offline: 'Jūs esat bezsaistē. Ieraksts paliek šajā ierīcē.',
    timeout: 'Transkripcija aizņēma pārāk ilgu laiku. Ieraksts paliek šajā ierīcē.',
    noAudio: 'Runa netika sadzirdēta.',
    permissionDenied: 'Atļaujiet mikrofonu pārlūka vai ierīces iestatījumos un mēģiniet vēlreiz.',
    insecureContext: 'Diktēšanai nepieciešams drošs savienojums (https).',
    noMicrophone: 'Mikrofons netika atrasts.',
    microphoneBusy: 'Mikrofonu izmanto cita lietotne.',
    deviceLost: 'Mikrofons tika atvienots.',
    pausedHidden: 'Diktēšana apstājās, kad lapa tika paslēpta.',
    finishing: 'Pabeidz tekstu…',
    tooLong: 'Diktēšana sasniedza laika ierobežojumu. Ieraksts paliek šajā ierīcē.',
    unsupported: 'Šī pārlūkprogramma nevar ierakstīt skaņu diktēšanai.',
    companyRequired: 'Diktēšana ir pieejama uzņēmuma darbvietā.',
    replaced: 'Diktēšana tika sākta citur. Ieraksts paliek šajā ierīcē.',
    paymentRequired:
      'Jūsu uzņēmuma lietošanas kredīts ir izlietots. Administrators var pievienot karti, pēc tam mēģiniet vēlreiz.',
  },
  lt: {
    start: 'Diktuoti',
    insert: 'Įterpti',
    cancelDictation: 'Atšaukti diktavimą',
    finish: 'Baigti diktavimą',
    retry: 'Bandyti dar kartą',
    discard: 'Atmesti',
    starting: 'Paleidžiamas mikrofonas…',
    listening: 'Klausomasi…',
    reconnecting: 'Jungiamasi iš naujo… Įrašas lieka šiame įrenginyje.',
    inserted: 'Įterpta',
    keptForRetry: 'Įrašas lieka šiame įrenginyje. Bandykite dar kartą arba jį atmeskite.',
    pendingInsert: 'Diktuotas tekstas paruoštas. Įterpkite arba atmeskite jį.',
    idleStopping: 'Kalbos negirdėti. Sustos po {seconds} s.',
    limitWarning: 'Diktavimas baigsis po {seconds} s.',
    captionLabel: 'Tiesioginis diktavimas',
    degraded: 'Perjungta į atsarginę transkripcijos paslaugą.',
    micHeldByMeeting: 'Mikrofonas įrašinėja susitikimą.',
    unavailable: 'Diktavimas šiuo metu nepasiekiamas.',
    originNotAllowed:
      'Šio puslapio kilmė neleidžiama diktavimui. Atidarykite Chat iš patvirtinto adreso.',
    providerUnavailable: 'Kalbos paslauga laikinai nepasiekiama. Netrukus bandykite dar kartą.',
    notConfigured: 'Diktavimas šiame serveryje nenustatytas.',
    signInAgain: 'Norėdami diktuoti, prisijunkite iš naujo.',
    rateLimited: 'Per daug diktavimų. Bandykite dar kartą po {seconds} s.',
    budgetExhausted: 'Šiandienos diktavimo kvota išnaudota.',
    offline: 'Esate neprisijungę. Įrašas lieka šiame įrenginyje.',
    timeout: 'Transkripcija užtruko per ilgai. Įrašas lieka šiame įrenginyje.',
    noAudio: 'Kalbos negirdėta.',
    permissionDenied:
      'Leiskite mikrofoną naršyklės arba įrenginio nustatymuose ir bandykite dar kartą.',
    insecureContext: 'Diktavimui reikia saugaus ryšio (https).',
    noMicrophone: 'Mikrofonas nerastas.',
    microphoneBusy: 'Mikrofoną naudoja kita programėlė.',
    deviceLost: 'Mikrofonas buvo atjungtas.',
    pausedHidden: 'Diktavimas sustojo, kai puslapis buvo paslėptas.',
    finishing: 'Baigiamas tekstas…',
    tooLong: 'Diktavimas pasiekė laiko ribą. Įrašas lieka šiame įrenginyje.',
    unsupported: 'Ši naršyklė negali įrašyti garso diktavimui.',
    companyRequired: 'Diktavimas pasiekiamas įmonės darbo erdvėje.',
    replaced: 'Diktavimas pradėtas kitur. Įrašas lieka šiame įrenginyje.',
    paymentRequired:
      'Jūsų įmonės naudojimo kreditas išnaudotas. Administratorius gali pridėti kortelę, tada bandykite dar kartą.',
  },
  mt: {
    start: 'Iddetta',
    insert: 'Daħħal',
    cancelDictation: 'Ikkanċella d-dettatura',
    finish: 'Temm id-dettatura',
    retry: 'Erġa’ pprova',
    discard: 'Armi',
    starting: 'Qed jinxtegħel il-mikrofonu…',
    listening: 'Qed nisma’…',
    reconnecting: 'Qed nerġa’ nqabbad… Ir-reġistrazzjoni tibqa’ fuq dan it-tagħmir.',
    inserted: 'Iddaħħal',
    keptForRetry: 'Ir-reġistrazzjoni tibqa’ fuq dan it-tagħmir. Erġa’ pprova jew armiha.',
    pendingInsert: 'Id-dettatura tiegħek lesta. Daħħalha jew armiha.',
    idleStopping: 'Ma jinstema’ l-ebda diskors. Jieqaf fi {seconds} s.',
    limitWarning: 'Id-dettatura tieqaf fi {seconds} s.',
    captionLabel: 'Dettatura diretta',
    degraded: 'Inqlibna għal servizz ta’ traskrizzjoni ta’ riżerva.',
    micHeldByMeeting: 'Il-mikrofonu qed jirreġistra laqgħa.',
    unavailable: 'Id-dettatura mhijiex disponibbli bħalissa.',
    originNotAllowed:
      'L-oriġini ta’ din il-paġna mhijiex permessa għad-dettatura. Iftaħ Chat mill-indirizz approvat.',
    providerUnavailable:
      'Is-servizz tad-diskors temporanjament mhuwiex disponibbli. Erġa’ pprova dalwaqt.',
    notConfigured: 'Id-dettatura mhijiex imwaqqfa fuq dan is-server.',
    signInAgain: 'Idħol mill-ġdid biex tiddetta.',
    rateLimited: 'Wisq dettaturi. Erġa’ pprova fi {seconds} s.',
    budgetExhausted: 'Il-kwota tad-dettatura tal-lum spiċċat.',
    offline: 'M’intix konness. Ir-reġistrazzjoni tibqa’ fuq dan it-tagħmir.',
    timeout: 'It-traskrizzjoni ħadet wisq żmien. Ir-reġistrazzjoni tibqa’ fuq dan it-tagħmir.',
    noAudio: 'Ma nstema’ l-ebda diskors.',
    permissionDenied:
      'Ippermetti l-mikrofonu fis-settings tal-browser jew tat-tagħmir u erġa’ pprova.',
    insecureContext: 'Id-dettatura teħtieġ konnessjoni sigura (https).',
    noMicrophone: 'Ma nstab l-ebda mikrofonu.',
    microphoneBusy: 'Il-mikrofonu qed jintuża minn app oħra.',
    deviceLost: 'Il-mikrofonu ġie skonnettjat.',
    pausedHidden: 'Id-dettatura waqfet meta l-paġna nħbiet.',
    finishing: 'Qed jitlesta t-test…',
    tooLong: 'Id-dettatura laħqet il-limitu tal-ħin. Ir-reġistrazzjoni tibqa’ fuq dan it-tagħmir.',
    unsupported: 'Dan il-browser ma jistax jirreġistra l-awdjo għad-dettatura.',
    companyRequired: 'Id-dettatura hija disponibbli fi spazju tax-xogħol tal-kumpanija.',
    replaced: 'Bdiet dettatura x’imkien ieħor. Ir-reġistrazzjoni tibqa’ fuq dan it-tagħmir.',
    paymentRequired:
      'Il-kreditu tal-użu tal-kumpanija tiegħek spiċċa. Amministratur jista’ jżid karta, imbagħad erġa’ pprova.',
  },
  pl: {
    start: 'Dyktuj',
    insert: 'Wstaw',
    cancelDictation: 'Anuluj dyktowanie',
    finish: 'Zakończ dyktowanie',
    retry: 'Spróbuj ponownie',
    discard: 'Odrzuć',
    starting: 'Uruchamianie mikrofonu…',
    listening: 'Słucham…',
    reconnecting: 'Ponowne łączenie… Nagranie pozostaje na tym urządzeniu.',
    inserted: 'Wstawiono',
    keptForRetry: 'Nagranie pozostaje na tym urządzeniu. Spróbuj ponownie lub je odrzuć.',
    pendingInsert: 'Dyktowany tekst jest gotowy. Wstaw go lub odrzuć.',
    idleStopping: 'Nie słychać mowy. Zatrzymanie za {seconds} s.',
    limitWarning: 'Dyktowanie zakończy się za {seconds} s.',
    captionLabel: 'Dyktowanie na żywo',
    degraded: 'Przełączono na zapasową usługę transkrypcji.',
    micHeldByMeeting: 'Mikrofon nagrywa spotkanie.',
    unavailable: 'Dyktowanie jest teraz niedostępne.',
    originNotAllowed:
      'Pochodzenie tej strony nie jest dozwolone dla dyktowania. Otwórz Chat z zatwierdzonego adresu.',
    providerUnavailable: 'Usługa mowy jest tymczasowo niedostępna. Spróbuj ponownie wkrótce.',
    notConfigured: 'Dyktowanie nie jest skonfigurowane na tym serwerze.',
    signInAgain: 'Zaloguj się ponownie, aby dyktować.',
    rateLimited: 'Zbyt wiele dyktowań. Spróbuj ponownie za {seconds} s.',
    budgetExhausted: 'Dzisiejszy limit dyktowania został wykorzystany.',
    offline: 'Jesteś offline. Nagranie pozostaje na tym urządzeniu.',
    timeout: 'Transkrypcja trwała zbyt długo. Nagranie pozostaje na tym urządzeniu.',
    noAudio: 'Nie wykryto mowy.',
    permissionDenied:
      'Zezwól na mikrofon w ustawieniach przeglądarki lub urządzenia i spróbuj ponownie.',
    insecureContext: 'Dyktowanie wymaga bezpiecznego połączenia (https).',
    noMicrophone: 'Nie znaleziono mikrofonu.',
    microphoneBusy: 'Mikrofon jest używany przez inną aplikację.',
    deviceLost: 'Mikrofon został odłączony.',
    pausedHidden: 'Dyktowanie zatrzymało się po ukryciu strony.',
    finishing: 'Kończenie tekstu…',
    tooLong: 'Dyktowanie osiągnęło limit czasu. Nagranie pozostaje na tym urządzeniu.',
    unsupported: 'Ta przeglądarka nie może nagrywać dźwięku do dyktowania.',
    companyRequired: 'Dyktowanie jest dostępne w firmowym obszarze roboczym.',
    replaced: 'Dyktowanie rozpoczęto w innym miejscu. Nagranie pozostaje na tym urządzeniu.',
    paymentRequired:
      'Kredyt na korzystanie Twojej firmy się wyczerpał. Administrator może dodać kartę, a potem spróbuj ponownie.',
  },
  pt: {
    start: 'Ditar',
    insert: 'Inserir',
    cancelDictation: 'Cancelar o ditado',
    finish: 'Terminar o ditado',
    retry: 'Tentar novamente',
    discard: 'Descartar',
    starting: 'A iniciar o microfone…',
    listening: 'A ouvir…',
    reconnecting: 'A restabelecer a ligação… A gravação fica guardada neste dispositivo.',
    inserted: 'Inserido',
    keptForRetry: 'A gravação fica guardada neste dispositivo. Tente novamente ou descarte-a.',
    pendingInsert: 'O seu ditado está pronto. Insira-o ou descarte-o.',
    idleStopping: 'Não se ouve fala. A parar dentro de {seconds} s.',
    limitWarning: 'O ditado para dentro de {seconds} s.',
    captionLabel: 'Ditado em direto',
    degraded: 'Mudou para um serviço de transcrição de reserva.',
    micHeldByMeeting: 'O microfone está a gravar uma reunião.',
    unavailable: 'O ditado não está disponível neste momento.',
    originNotAllowed:
      'A origem desta página não é permitida para ditado. Abra o Chat pelo endereço aprovado.',
    providerUnavailable:
      'O serviço de voz está temporariamente indisponível. Tente novamente em breve.',
    notConfigured: 'O ditado não está configurado neste servidor.',
    signInAgain: 'Inicie sessão novamente para ditar.',
    rateLimited: 'Demasiados ditados. Tente novamente dentro de {seconds} s.',
    budgetExhausted: 'A quota de ditado de hoje esgotou-se.',
    offline: 'Está offline. A gravação fica guardada neste dispositivo.',
    timeout: 'A transcrição demorou demasiado. A gravação fica guardada neste dispositivo.',
    noAudio: 'Não se ouviu nenhuma fala.',
    permissionDenied:
      'Permita o microfone nas definições do navegador ou do dispositivo e tente novamente.',
    insecureContext: 'O ditado requer uma ligação segura (https).',
    noMicrophone: 'Não foi encontrado nenhum microfone.',
    microphoneBusy: 'O microfone está a ser usado por outra aplicação.',
    deviceLost: 'O microfone foi desligado.',
    pausedHidden: 'O ditado parou quando a página foi ocultada.',
    finishing: 'A concluir o texto…',
    tooLong: 'O ditado atingiu o limite de tempo. A gravação fica guardada neste dispositivo.',
    unsupported: 'Este navegador não consegue gravar áudio para ditado.',
    companyRequired: 'O ditado está disponível num espaço de trabalho da empresa.',
    replaced: 'Foi iniciado um ditado noutro local. A gravação fica guardada neste dispositivo.',
    paymentRequired:
      'O crédito de utilização da sua empresa esgotou-se. Um administrador pode adicionar um cartão; depois tente novamente.',
  },
  ro: {
    start: 'Dictează',
    insert: 'Inserează',
    cancelDictation: 'Anulează dictarea',
    finish: 'Încheie dictarea',
    retry: 'Încearcă din nou',
    discard: 'Renunță',
    starting: 'Se pornește microfonul…',
    listening: 'Se ascultă…',
    reconnecting: 'Se reconectează… Înregistrarea rămâne pe acest dispozitiv.',
    inserted: 'Inserat',
    keptForRetry: 'Înregistrarea rămâne pe acest dispozitiv. Încearcă din nou sau renunță la ea.',
    pendingInsert: 'Dictarea este gata. Inserează-o sau renunță la ea.',
    idleStopping: 'Nu se aude vorbire. Oprire în {seconds} s.',
    limitWarning: 'Dictarea se oprește în {seconds} s.',
    captionLabel: 'Dictare în timp real',
    degraded: 'S-a trecut la un serviciu de transcriere de rezervă.',
    micHeldByMeeting: 'Microfonul înregistrează o ședință.',
    unavailable: 'Dictarea nu este disponibilă acum.',
    originNotAllowed:
      'Originea acestei pagini nu este permisă pentru dictare. Deschide Chat de la adresa aprobată.',
    providerUnavailable: 'Serviciul vocal este temporar indisponibil. Încearcă din nou în curând.',
    notConfigured: 'Dictarea nu este configurată pe acest server.',
    signInAgain: 'Autentifică-te din nou pentru a dicta.',
    rateLimited: 'Prea multe dictări. Încearcă din nou în {seconds} s.',
    budgetExhausted: 'Cota de dictare de azi s-a epuizat.',
    offline: 'Ești offline. Înregistrarea rămâne pe acest dispozitiv.',
    timeout: 'Transcrierea a durat prea mult. Înregistrarea rămâne pe acest dispozitiv.',
    noAudio: 'Nu s-a auzit vorbire.',
    permissionDenied:
      'Permite microfonul în setările browserului sau ale dispozitivului și încearcă din nou.',
    insecureContext: 'Dictarea necesită o conexiune securizată (https).',
    noMicrophone: 'Nu a fost găsit niciun microfon.',
    microphoneBusy: 'Microfonul este folosit de altă aplicație.',
    deviceLost: 'Microfonul a fost deconectat.',
    pausedHidden: 'Dictarea s-a oprit când pagina a fost ascunsă.',
    finishing: 'Se finalizează textul…',
    tooLong: 'Dictarea a atins limita de timp. Înregistrarea rămâne pe acest dispozitiv.',
    unsupported: 'Acest browser nu poate înregistra audio pentru dictare.',
    companyRequired: 'Dictarea este disponibilă într-un spațiu de lucru al companiei.',
    replaced: 'O dictare a început în altă parte. Înregistrarea rămâne pe acest dispozitiv.',
    paymentRequired:
      'Creditul de utilizare al companiei tale s-a epuizat. Un administrator poate adăuga un card, apoi încearcă din nou.',
  },
  sk: {
    start: 'Diktovať',
    insert: 'Vložiť',
    cancelDictation: 'Zrušiť diktovanie',
    finish: 'Dokončiť diktovanie',
    retry: 'Skúsiť znova',
    discard: 'Zahodiť',
    starting: 'Spúšťa sa mikrofón…',
    listening: 'Počúvam…',
    reconnecting: 'Obnovuje sa pripojenie… Nahrávka zostáva v tomto zariadení.',
    inserted: 'Vložené',
    keptForRetry: 'Nahrávka zostáva v tomto zariadení. Skúste to znova alebo ju zahoďte.',
    pendingInsert: 'Diktát je pripravený. Vložte ho alebo ho zahoďte.',
    idleStopping: 'Nie je počuť reč. Zastaví sa o {seconds} s.',
    limitWarning: 'Diktovanie skončí o {seconds} s.',
    captionLabel: 'Živé diktovanie',
    degraded: 'Prepnuté na záložnú službu prepisu.',
    micHeldByMeeting: 'Mikrofón nahráva stretnutie.',
    unavailable: 'Diktovanie teraz nie je k dispozícii.',
    originNotAllowed:
      'Pôvod tejto stránky nie je povolený pre diktovanie. Otvorte Chat zo schválenej adresy.',
    providerUnavailable: 'Hlasová služba je dočasne nedostupná. Skúste to znova čoskoro.',
    notConfigured: 'Diktovanie nie je na tomto serveri nastavené.',
    signInAgain: 'Ak chcete diktovať, znova sa prihláste.',
    rateLimited: 'Príliš veľa diktovaní. Skúste to znova o {seconds} s.',
    budgetExhausted: 'Dnešný limit diktovania je vyčerpaný.',
    offline: 'Ste offline. Nahrávka zostáva v tomto zariadení.',
    timeout: 'Prepis trval príliš dlho. Nahrávka zostáva v tomto zariadení.',
    noAudio: 'Nebolo počuť žiadnu reč.',
    permissionDenied:
      'Povoľte mikrofón v nastaveniach prehliadača alebo zariadenia a skúste to znova.',
    insecureContext: 'Diktovanie vyžaduje zabezpečené pripojenie (https).',
    noMicrophone: 'Nenašiel sa žiadny mikrofón.',
    microphoneBusy: 'Mikrofón používa iná aplikácia.',
    deviceLost: 'Mikrofón bol odpojený.',
    pausedHidden: 'Diktovanie sa zastavilo, keď bola stránka skrytá.',
    finishing: 'Dokončuje sa text…',
    tooLong: 'Diktovanie dosiahlo časový limit. Nahrávka zostáva v tomto zariadení.',
    unsupported: 'Tento prehliadač nedokáže nahrávať zvuk na diktovanie.',
    companyRequired: 'Diktovanie je k dispozícii vo firemnom pracovnom priestore.',
    replaced: 'Diktovanie sa začalo inde. Nahrávka zostáva v tomto zariadení.',
    paymentRequired:
      'Kredit na využívanie vašej firmy je vyčerpaný. Správca môže pridať kartu, potom to skúste znova.',
  },
  sl: {
    start: 'Nareci',
    insert: 'Vstavi',
    cancelDictation: 'Prekliči narekovanje',
    finish: 'Končaj narekovanje',
    retry: 'Poskusi znova',
    discard: 'Zavrzi',
    starting: 'Zaganjanje mikrofona…',
    listening: 'Poslušam…',
    reconnecting: 'Ponovno povezovanje… Posnetek ostane v tej napravi.',
    inserted: 'Vstavljeno',
    keptForRetry: 'Posnetek ostane v tej napravi. Poskusite znova ali ga zavrzite.',
    pendingInsert: 'Narek je pripravljen. Vstavite ga ali ga zavrzite.',
    idleStopping: 'Govora ni slišati. Ustavitev čez {seconds} s.',
    limitWarning: 'Narekovanje se konča čez {seconds} s.',
    captionLabel: 'Narekovanje v živo',
    degraded: 'Preklopljeno na rezervno storitev prepisa.',
    micHeldByMeeting: 'Mikrofon snema sestanek.',
    unavailable: 'Narekovanje trenutno ni na voljo.',
    originNotAllowed:
      'Izvor te strani ni dovoljen za narekovanje. Odprite Chat z odobrenega naslova.',
    providerUnavailable: 'Govorna storitev začasno ni na voljo. Kmalu poskusite znova.',
    notConfigured: 'Narekovanje na tem strežniku ni nastavljeno.',
    signInAgain: 'Za narekovanje se znova prijavite.',
    rateLimited: 'Preveč narekov. Poskusite znova čez {seconds} s.',
    budgetExhausted: 'Današnja kvota narekovanja je porabljena.',
    offline: 'Niste povezani. Posnetek ostane v tej napravi.',
    timeout: 'Prepis je trajal predolgo. Posnetek ostane v tej napravi.',
    noAudio: 'Govora ni bilo slišati.',
    permissionDenied: 'Dovolite mikrofon v nastavitvah brskalnika ali naprave in poskusite znova.',
    insecureContext: 'Narekovanje zahteva varno povezavo (https).',
    noMicrophone: 'Mikrofona ni bilo mogoče najti.',
    microphoneBusy: 'Mikrofon uporablja druga aplikacija.',
    deviceLost: 'Mikrofon je bil odklopljen.',
    pausedHidden: 'Narekovanje se je ustavilo, ko je bila stran skrita.',
    finishing: 'Dokončevanje besedila…',
    tooLong: 'Narekovanje je doseglo časovno omejitev. Posnetek ostane v tej napravi.',
    unsupported: 'Ta brskalnik ne more snemati zvoka za narekovanje.',
    companyRequired: 'Narekovanje je na voljo v delovnem prostoru podjetja.',
    replaced: 'Narekovanje se je začelo drugje. Posnetek ostane v tej napravi.',
    paymentRequired:
      'Kredit za uporabo vašega podjetja je porabljen. Skrbnik lahko doda kartico, nato poskusite znova.',
  },
  es: {
    start: 'Dictar',
    insert: 'Insertar',
    cancelDictation: 'Cancelar el dictado',
    finish: 'Terminar el dictado',
    retry: 'Reintentar',
    discard: 'Descartar',
    starting: 'Iniciando el micrófono…',
    listening: 'Escuchando…',
    reconnecting: 'Reconectando… La grabación se conserva en este dispositivo.',
    inserted: 'Insertado',
    keptForRetry: 'La grabación se conserva en este dispositivo. Reinténtalo o descártala.',
    pendingInsert: 'Tu dictado está listo. Insértalo o descártalo.',
    idleStopping: 'No se oye voz. Se detendrá en {seconds} s.',
    limitWarning: 'El dictado se detendrá en {seconds} s.',
    captionLabel: 'Dictado en directo',
    degraded: 'Se ha cambiado a un servicio de transcripción de respaldo.',
    micHeldByMeeting: 'El micrófono está grabando una reunión.',
    unavailable: 'El dictado no está disponible en este momento.',
    originNotAllowed:
      'El origen de esta página no está permitido para dictar. Abre Chat desde la dirección aprobada.',
    providerUnavailable:
      'El servicio de voz no está disponible temporalmente. Inténtalo de nuevo pronto.',
    notConfigured: 'El dictado no está configurado en este servidor.',
    signInAgain: 'Vuelve a iniciar sesión para dictar.',
    rateLimited: 'Demasiados dictados. Inténtalo de nuevo en {seconds} s.',
    budgetExhausted: 'Se ha agotado el cupo de dictado de hoy.',
    offline: 'Estás sin conexión. La grabación se conserva en este dispositivo.',
    timeout: 'La transcripción tardó demasiado. La grabación se conserva en este dispositivo.',
    noAudio: 'No se oyó ninguna voz.',
    permissionDenied:
      'Permite el micrófono en los ajustes del navegador o del dispositivo e inténtalo de nuevo.',
    insecureContext: 'El dictado necesita una conexión segura (https).',
    noMicrophone: 'No se encontró ningún micrófono.',
    microphoneBusy: 'Otra aplicación está usando el micrófono.',
    deviceLost: 'El micrófono se ha desconectado.',
    pausedHidden: 'El dictado se detuvo al ocultarse la página.',
    finishing: 'Terminando el texto…',
    tooLong:
      'El dictado alcanzó su límite de tiempo. La grabación se conserva en este dispositivo.',
    unsupported: 'Este navegador no puede grabar audio para dictar.',
    companyRequired: 'El dictado está disponible en un espacio de trabajo de empresa.',
    replaced: 'Se inició un dictado en otro lugar. La grabación se conserva en este dispositivo.',
    paymentRequired:
      'El crédito de uso de tu empresa se ha agotado. Un administrador puede añadir una tarjeta; después, vuelve a intentarlo.',
  },
  sv: {
    start: 'Tala in',
    insert: 'Infoga',
    cancelDictation: 'Avbryt dikteringen',
    finish: 'Avsluta dikteringen',
    retry: 'Försök igen',
    discard: 'Släng',
    starting: 'Startar mikrofonen…',
    listening: 'Lyssnar…',
    reconnecting: 'Återansluter… Ljudet finns kvar på den här enheten.',
    inserted: 'Infogat',
    keptForRetry: 'Inspelningen finns kvar på den här enheten. Försök igen eller släng den.',
    pendingInsert: 'Din diktering är klar. Infoga den eller släng den.',
    idleStopping: 'Inget tal hörs. Stoppar om {seconds} s.',
    limitWarning: 'Dikteringen stoppas om {seconds} s.',
    captionLabel: 'Diktering i realtid',
    degraded: 'Bytte till en reservtjänst för transkribering.',
    micHeldByMeeting: 'Mikrofonen spelar in ett möte.',
    unavailable: 'Diktering är inte tillgänglig just nu.',
    originNotAllowed:
      'Den här sidans ursprung är inte tillåtet för diktering. Öppna Chat från den godkända adressen.',
    providerUnavailable: 'Taltjänsten är tillfälligt otillgänglig. Försök igen om en stund.',
    notConfigured: 'Diktering är inte konfigurerad på den här servern.',
    signInAgain: 'Logga in igen för att diktera.',
    rateLimited: 'För många dikteringar. Försök igen om {seconds} s.',
    budgetExhausted: 'Dagens dikteringskvot är slut.',
    offline: 'Du är offline. Inspelningen finns kvar på den här enheten.',
    timeout: 'Transkriberingen tog för lång tid. Inspelningen finns kvar på den här enheten.',
    noAudio: 'Inget tal hördes.',
    permissionDenied:
      'Tillåt mikrofonen i webbläsarens eller enhetens inställningar och försök igen.',
    insecureContext: 'Diktering kräver en säker anslutning (https).',
    noMicrophone: 'Ingen mikrofon hittades.',
    microphoneBusy: 'Mikrofonen används av en annan app.',
    deviceLost: 'Mikrofonen kopplades från.',
    pausedHidden: 'Dikteringen stoppades när sidan doldes.',
    finishing: 'Gör klart texten…',
    tooLong: 'Dikteringen nådde tidsgränsen. Inspelningen finns kvar på den här enheten.',
    unsupported: 'Den här webbläsaren kan inte spela in ljud för diktering.',
    companyRequired: 'Diktering finns i en företagsarbetsyta.',
    replaced:
      'En diktering startades någon annanstans. Inspelningen finns kvar på den här enheten.',
    paymentRequired:
      'Företagets användningskredit är slut. En administratör kan lägga till ett kort, försök sedan igen.',
  },
};
export default speechCopy;
